<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { minValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { NextButton },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isEnterprise } = useConfig();
    return { v$: useVuelidate(), isEnterprise };
  },
  data() {
    return {
      selectedAgents: [],
      isAgentListUpdating: false,
      enableAutoAssignment: false,
      maxAssignmentLimit: null,
    };
  },
  computed: {
    ...mapGetters({ agentList: 'agents/getAgents' }),
    maxAssignmentLimitErrors() {
      if (this.v$.maxAssignmentLimit.$error) {
        return this.$t(
          'INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_RANGE_ERROR'
        );
      }
      return '';
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.enableAutoAssignment = this.inbox.enable_auto_assignment;
      this.maxAssignmentLimit =
        this.inbox?.auto_assignment_config?.max_assignment_limit || null;
      this.fetchAttachedAgents();
    },
    async fetchAttachedAgents() {
      try {
        const response = await this.$store.dispatch('inboxMembers/get', {
          inboxId: this.inbox.id,
        });
        const {
          data: { payload: inboxMembers },
        } = response;
        this.selectedAgents = inboxMembers;
      } catch (_) {}
    },
    handleEnableAutoAssignment() {
      this.updateInbox();
    },
    async updateAgents() {
      const agentList = this.selectedAgents.map(el => el.id);
      this.isAgentListUpdating = true;
      try {
        await this.$store.dispatch('inboxMembers/create', {
          inboxId: this.inbox.id,
          agentList,
        });
        useAlert(this.$t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        useAlert(this.$t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
      this.isAgentListUpdating = false;
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          enable_auto_assignment: this.enableAutoAssignment,
          auto_assignment_config: {
            max_assignment_limit: this.maxAssignmentLimit,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      }
    },
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
    maxAssignmentLimit: {
      minValue: minValue(1),
    },
  },
};
</script>

<template>
  <div class="col-page">
    <div class="col-body">

      <!-- ── Page header ── -->
      <div class="col-page-hd">
        <h2 class="col-page-title">Collaborators</h2>
        <p class="col-page-desc">
          Manage the agents assigned to this inbox and configure auto-assignment
          settings.
        </p>
      </div>

      <!-- ── Section: Inbox Agents ── -->
      <section class="col-section">
        <div class="col-section__hd">
          <h3 class="col-section__title">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS') }}
          </h3>
          <p class="col-section__desc">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS_SUB_TEXT') }}
          </p>
        </div>

        <div class="col-section__bd">
          <div class="col-field">
            <label class="col-label">
              {{ $t('INBOX_MGMT.ADD.AGENTS.PICK_AGENTS') }}
            </label>
            <multiselect
              v-model="selectedAgents"
              class="col-multiselect"
              :options="agentList"
              track-by="id"
              label="name"
              multiple
              :close-on-select="false"
              :clear-on-select="false"
              hide-selected
              :placeholder="$t('INBOX_MGMT.ADD.AGENTS.PICK_AGENTS')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              @select="v$.selectedAgents.$touch"
            />
          </div>

          <div class="col-actions">
            <button
              class="col-btn col-btn--primary"
              type="button"
              :disabled="isAgentListUpdating"
              @click="updateAgents"
            >
              <span
                v-if="isAgentListUpdating"
                class="col-btn__spinner"
              />
              {{ isAgentListUpdating ? '…' : $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE') }}
            </button>
          </div>
        </div>
      </section>

      <!-- ── Section: Auto Assignment ── -->
      <section class="col-section">
        <div class="col-section__hd">
          <h3 class="col-section__title">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT') }}
          </h3>
          <p class="col-section__desc">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT_SUB_TEXT') }}
          </p>
        </div>

        <div class="col-section__bd">
          <label class="col-toggle-row">
            <span class="col-toggle-wrap">
              <input
                id="enableAutoAssignment"
                v-model="enableAutoAssignment"
                type="checkbox"
                class="col-checkbox-input"
                @change="handleEnableAutoAssignment"
              />
              <span
                class="col-toggle-track"
                :class="{ 'col-toggle-track--on': enableAutoAssignment }"
              >
                <span class="col-toggle-knob" />
              </span>
            </span>
            <span class="col-toggle-text">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT') }}
            </span>
          </label>
          <p class="col-hint">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT_SUB_TEXT') }}
          </p>

          <div v-if="enableAutoAssignment && isEnterprise" class="col-limit-wrap">
            <div class="col-field">
              <label class="col-label">
                {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT') }}
              </label>
              <input
                v-model="maxAssignmentLimit"
                type="number"
                class="col-input"
                :class="{ 'col-input--error': v$.maxAssignmentLimit.$error }"
                min="1"
                @blur="v$.maxAssignmentLimit.$touch"
              />
              <p v-if="maxAssignmentLimitErrors" class="col-error">
                {{ maxAssignmentLimitErrors }}
              </p>
              <p class="col-hint">
                {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_SUB_TEXT') }}
              </p>
            </div>
            <div class="col-actions">
              <button
                class="col-btn col-btn--primary"
                type="button"
                :disabled="v$.maxAssignmentLimit.$invalid"
                @click="updateInbox"
              >
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE') }}
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<style scoped>
/* ── Page header ── */
.col-page-hd {
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.col-page-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}

.col-page-desc {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0;
  line-height: 1.5;
}

/* ── Page shell ── */
.col-page {
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
}

.col-body {
  max-width: 720px;
  margin: 0;
  padding: 32px 32px 80px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Section ── */
.col-section {
  padding: 28px 0;
  border-bottom: 1px solid #e5e7eb;
}

.col-section:last-child {
  border-bottom: none;
}

.col-section__hd {
  margin-bottom: 20px;
}

.col-section__title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}

.col-section__desc {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
  line-height: 1.5;
}

.col-section__bd {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Field ── */
.col-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.col-label {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}

.col-input {
  width: 100%;
  max-width: 320px;
  padding: 9px 13px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #111827;
  background: #ffffff;
  outline: none;
  transition: border-color 0.15s;
}

.col-input:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.12);
}

.col-input--error {
  border-color: #ef4444;
}

.col-error {
  font-size: 12px;
  color: #ef4444;
  margin: 0;
}

.col-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
  line-height: 1.5;
}

/* ── Multiselect overrides ── */
.col-multiselect :deep(.multiselect__tags) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 8px !important;
  min-height: 42px !important;
}

.col-multiselect :deep(.multiselect__input),
.col-multiselect :deep(.multiselect__single) {
  background: #ffffff !important;
  color: #111827 !important;
}

.col-multiselect :deep(.multiselect__placeholder) {
  color: #9ca3af !important;
}

.col-multiselect :deep(.multiselect__tag) {
  background: #4384f5 !important;
  color: #ffffff !important;
  border-radius: 6px !important;
}

.col-multiselect :deep(.multiselect__tag-icon::after) {
  color: #ffffff !important;
}

.col-multiselect :deep(.multiselect__content-wrapper) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 8px !important;
}

.col-multiselect :deep(.multiselect__option) {
  color: #111827 !important;
  background: transparent !important;
}

.col-multiselect :deep(.multiselect__option--highlight) {
  background: #eaf2ff !important;
  color: #111827 !important;
}

/* ── Toggle switch ── */
.col-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.col-toggle-wrap {
  position: relative;
  flex-shrink: 0;
}

.col-checkbox-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.col-toggle-track {
  display: flex;
  align-items: center;
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: #d1d5db;
  padding: 2px;
  transition: background 0.2s;
  cursor: pointer;
}

.col-toggle-track--on {
  background: #4384f5;
}

.col-toggle-knob {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}

.col-toggle-track--on .col-toggle-knob {
  transform: translateX(16px);
}

.col-toggle-text {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

/* ── Limit block ── */
.col-limit-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

/* ── Actions ── */
.col-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.col-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  border: 1px solid transparent;
}

.col-btn--primary {
  background: #111827;
  color: #ffffff;
  border-color: #111827;
}

.col-btn--primary:hover:not(:disabled) {
  background: #1f2937;
}

.col-btn--primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.col-btn__spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: col-spin 0.7s linear infinite;
}

@keyframes col-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
